/**
 * Cliente HTTP da API do KAVI.
 *
 * Usa `fetch` nativo em vez de uma biblioteca externa. O token de acesso é
 * mantido em memória (não em localStorage), reduzindo a exposição a XSS; a
 * renovação via refresh token entra na Fase 2, junto com a autenticação.
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

export class ErroApi extends Error {
  readonly status: number;
  readonly mensagem: string | string[];
  readonly codigo?: string;
  readonly requestId?: string;

  constructor(status: number, mensagem: string | string[], codigo?: string, requestId?: string) {
    super(Array.isArray(mensagem) ? mensagem.join('; ') : mensagem);
    this.name = 'ErroApi';
    this.status = status;
    this.mensagem = mensagem;
    this.codigo = codigo;
    this.requestId = requestId;
  }

  /** Mensagem única, pronta para exibição ao usuário. */
  get mensagemUsuario(): string {
    return Array.isArray(this.mensagem) ? this.mensagem.join('. ') : this.mensagem;
  }
}

let tokenAcesso: string | null = null;

export function definirTokenAcesso(token: string | null): void {
  tokenAcesso = token;
}

interface OpcoesRequisicao extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

export async function requisitar<T>(caminho: string, opcoes: OpcoesRequisicao = {}): Promise<T> {
  const { body, headers, ...resto } = opcoes;

  const resposta = await fetch(`${BASE_URL}${caminho}`, {
    ...resto,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(tokenAcesso ? { Authorization: `Bearer ${tokenAcesso}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: 'include',
  });

  if (resposta.status === 204) {
    return undefined as T;
  }

  const tipoConteudo = resposta.headers.get('content-type') ?? '';
  const dados: unknown = tipoConteudo.includes('application/json')
    ? await resposta.json()
    : await resposta.text();

  if (!resposta.ok) {
    const corpo = dados as {
      mensagem?: string | string[];
      erro?: string;
      requestId?: string;
    };

    throw new ErroApi(
      resposta.status,
      corpo?.mensagem ?? 'Não foi possível concluir a operação.',
      corpo?.erro,
      corpo?.requestId,
    );
  }

  return dados as T;
}

/**
 * Envio de formulário multipart (upload de anexo).
 *
 * Não passa por `requisitar` porque este serializa o corpo em JSON. O
 * `Content-Type` é deixado em branco de propósito: o navegador precisa
 * defini-lo com o boundary do multipart.
 */
async function enviarFormulario<T>(caminho: string, form: FormData): Promise<T> {
  const resposta = await fetch(`${BASE_URL}${caminho}`, {
    method: 'POST',
    headers: tokenAcesso ? { Authorization: `Bearer ${tokenAcesso}` } : {},
    body: form,
    credentials: 'include',
  });

  const dados: unknown = resposta.headers.get('content-type')?.includes('json')
    ? await resposta.json()
    : await resposta.text();

  if (!resposta.ok) {
    const corpo = dados as { mensagem?: string | string[]; erro?: string; requestId?: string };
    throw new ErroApi(
      resposta.status,
      corpo?.mensagem ?? 'Não foi possível enviar o arquivo.',
      corpo?.erro,
      corpo?.requestId,
    );
  }

  return dados as T;
}

/** Nome sugerido pelo servidor no `Content-Disposition`, quando houver. */
function nomeSugerido(resposta: Response): string | null {
  const cabecalho = resposta.headers.get('content-disposition');
  if (!cabecalho) return null;

  const comAspas = /filename="([^"]+)"/.exec(cabecalho);
  if (comAspas?.[1]) return comAspas[1];

  const semAspas = /filename=([^;]+)/.exec(cabecalho);
  return semAspas?.[1]?.trim() ?? null;
}

/** Baixa um recurso binário (anexo). */
async function baixarBlob(caminho: string): Promise<Blob> {
  const resposta = await fetch(`${BASE_URL}${caminho}`, {
    headers: tokenAcesso ? { Authorization: `Bearer ${tokenAcesso}` } : {},
    credentials: 'include',
  });

  if (!resposta.ok) {
    let mensagem = 'Não foi possível baixar o arquivo.';
    try {
      const corpo = (await resposta.json()) as { mensagem?: string };
      if (corpo.mensagem) mensagem = corpo.mensagem;
    } catch {
      // Resposta sem corpo JSON: fica a mensagem padrão.
    }
    throw new ErroApi(resposta.status, mensagem);
  }

  return resposta.blob();
}

/**
 * Baixa um arquivo gerado pelo servidor, trazendo junto o nome que ele deu.
 *
 * O nome de um CSV de relatório carrega bloco e período — remontá-lo no
 * cliente seria manter a mesma regra em dois lugares, e bastaria mudar um
 * deles para o arquivo passar a mentir sobre o que contém.
 */
async function baixarArquivo(caminho: string): Promise<{ blob: Blob; nome: string | null }> {
  const resposta = await fetch(`${BASE_URL}${caminho}`, {
    headers: tokenAcesso ? { Authorization: `Bearer ${tokenAcesso}` } : {},
    credentials: 'include',
  });

  if (!resposta.ok) {
    let mensagem = 'Não foi possível gerar o arquivo.';
    try {
      const corpo = (await resposta.json()) as { mensagem?: string | string[] };
      if (corpo.mensagem) {
        mensagem = Array.isArray(corpo.mensagem) ? corpo.mensagem.join('. ') : corpo.mensagem;
      }
    } catch {
      // Resposta sem corpo JSON: fica a mensagem padrão.
    }
    throw new ErroApi(resposta.status, mensagem);
  }

  return { blob: await resposta.blob(), nome: nomeSugerido(resposta) };
}

export const api = {
  get: <T>(caminho: string, opcoes?: OpcoesRequisicao) =>
    requisitar<T>(caminho, { ...opcoes, method: 'GET' }),
  postForm: <T = unknown>(caminho: string, form: FormData) => enviarFormulario<T>(caminho, form),
  getBlob: (caminho: string) => baixarBlob(caminho),
  getArquivo: (caminho: string) => baixarArquivo(caminho),
  post: <T>(caminho: string, body?: unknown, opcoes?: OpcoesRequisicao) =>
    requisitar<T>(caminho, { ...opcoes, method: 'POST', body }),
  patch: <T>(caminho: string, body?: unknown, opcoes?: OpcoesRequisicao) =>
    requisitar<T>(caminho, { ...opcoes, method: 'PATCH', body }),
  delete: <T>(caminho: string, opcoes?: OpcoesRequisicao) =>
    requisitar<T>(caminho, { ...opcoes, method: 'DELETE' }),
};
